import type {ReferralTrackingDetails} from 'src/types/referral-v2';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch} from 'react-redux';
import {
  getFormattedDate,
  createDownloadableUrl,
  isReferrerPortal,
} from 'src/components/referral-v2/common';

import {getResumeLink} from 'src/action-creators/referrer-portal/tracking';

import {
  Card,
  Panel,
  PanelHeader,
  PanelBody,
  Link,
  SubTitleLarge,
  PanelFooter,
  CardHeader,
  SubTitleMedium,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/index';
import ReferralDetailRow from 'src/components/referral-v2/table/referral-details-row';
import ProgressBar from 'src/components/referral-v2/tracking/progress-bar';
import css from 'src/components/referral-v2/tracking/tracking.css';


type GenericObject = {
  [key: string]: string;
};

const ReferralDetailSidePanel = ({
  referralTrackingDetails,
  onClose,
  isOpen,
  fileContainer,
  showProgressBar,
  showPersonalDetails,
  footer,
}: {
  referralTrackingDetails: ReferralTrackingDetails;
  onClose: (arg1: React.SyntheticEvent<HTMLElement>) => unknown;
  isOpen: boolean;
  fileContainer: string;
  showProgressBar?: boolean;
  showPersonalDetails?: boolean;
  footer?: React.ReactNode;
}) => {
  const {t, makeKey, language} = useLabelTranslation();
  const dispatch = useDispatch();
  const {
    name,
    email,
    contact,
    job_title,
    recommendation,
    referrer_name,
    referrer_email,
    custom_questions,
    resume,
    referred_on,
    source,
    status,
  } = referralTrackingDetails;
  return (
    <>
      <Panel
        anchor="right"
        size="large"
        initialFocus={1}
        onClose={onClose}
        tapOutsideToClose
        isOpen={isOpen}
        classNames={{backdrop: css.panelroot}}
      >
        <PanelHeader onCloseButtonClick={onClose}>
          <SubTitleLarge>
            {t(
              'Referral_and_Referrer_Details',
              'Referral and Referrer Details',
            )}
          </SubTitleLarge>
        </PanelHeader>
        <PanelBody className={css.flexColumn}>
          <Card classNames={{wrapper: css.sidePandelCard}}>
            <CardHeader>
              <SubTitleMedium>
                {t('Referral_Details', 'Referral Details')}
              </SubTitleMedium>
            </CardHeader>
            <CardContent className={css.sidePandelCardContent}>
              <ReferralDetailRow
                label={t('Name', 'Name')}
                value={name}
                autoTruncate
              />
              <ReferralDetailRow
                label={t('Email', 'Email')}
                value={email}
                autoTruncate
              />
              <ReferralDetailRow label={t('Phone', 'Phone')} value={contact} />
              <ReferralDetailRow
                label={t('Referred_Job', 'Referred Job')}
                value={job_title}
              />
              <ReferralDetailRow
                label={t('Referred_On', 'Referred On')}
                value={getFormattedDate(referred_on, language)}
              />
              <ReferralDetailRow
                label={t('Status', 'Status')}
                value={t(makeKey(status), status)}
              />
              <ReferralDetailRow
                label={t('Source', 'Source')}
                value={t(makeKey(source), source)}
              />
              {recommendation ? (
                <ReferralDetailRow
                  label={t('Recommendation', 'Recommendation')}
                  value={recommendation}
                />
              ) : null}
              {resume ? (
                <ReferralDetailRow
                  label={t('Resume', 'Resume')}
                  value={job_title}
                  customComponent={
                    isReferrerPortal() ? (
                      <Link onClick={() => dispatch(getResumeLink(resume))}>
                        {t('Download', 'Download')}
                      </Link>
                    ) : (
                      <Link
                        href={createDownloadableUrl(resume, fileContainer)}
                        type="_blank"
                      >
                        {t('Download', 'Download')}
                      </Link>
                    )
                  }
                />
              ) : null}
            </CardContent>
          </Card>
          {showProgressBar && <ProgressBar currentStatus={status} />}
          {custom_questions && custom_questions.length > 0 && (
            <>
              <Card classNames={{wrapper: css.sidePandelCard}}>
                <CardHeader>
                  <SubTitleMedium>
                    {t('Additional_Details', 'Additional Details')}
                  </SubTitleMedium>
                </CardHeader>
                <CardContent className={css.sidePandelCardContent}>
                  {custom_questions.map((customQuestion: GenericObject) => (
                    <React.Fragment key={Object.keys(customQuestion)[0]}>
                      <ReferralDetailRow
                        label={Object.keys(customQuestion).toString()}
                        value={Object.values(customQuestion).toString()}
                        autoTruncate
                      />
                    </React.Fragment>
                  ))}
                </CardContent>
              </Card>
            </>
          )}

          {showPersonalDetails && (
            <Card classNames={{wrapper: css.sidePandelCard}}>
              <CardHeader>
                <SubTitleMedium>
                  {t('Referrer_Details', 'Referrer Details')}
                </SubTitleMedium>
              </CardHeader>
              <CardContent className={css.sidePandelCardContent}>
                <ReferralDetailRow
                  label={t('Name', 'Name')}
                  value={referrer_name}
                  autoTruncate
                />
                <ReferralDetailRow
                  label={t('Email', 'Email')}
                  value={referrer_email}
                  autoTruncate
                />
              </CardContent>
            </Card>
          )}
        </PanelBody>
        {!!footer && <PanelFooter>{footer}</PanelFooter>}
      </Panel>
    </>
  );
};

export default ReferralDetailSidePanel;
